import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Logo, DonkeyMark } from "@/components/Logo";
import Navbar from "@/components/Navbar";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { PublicAmbient, KineticHeading } from "@/components/public/PublicVisuals";
import { IntelligencePipeline } from "@/components/public/IntelligencePipeline";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { track } from "@/lib/analytics";
import {
  ArrowRight, ArrowLeft, X, CheckCircle2, Trophy, FlaskConical, Hammer,
  Rocket, Sparkles, MessageSquare, Lock, Play, Pause, Users,
  Clock, Wallet, Briefcase, Target,
} from "lucide-react";

const AUTOPLAY_MS = 6500;
const EASE = [0.22, 1, 0.36, 1];

// ---- Clearly-illustrative fictional people (no real data) ----
const PERSONAS = [
  {
    id: "priya", name: "Priya", tagline: "Teaching assistant, evenings free",
    signals: [
      { icon: Clock, label: "Time", value: "~12 hrs/wk" },
      { icon: Wallet, label: "Budget", value: "£500" },
      { icon: Briefcase, label: "Experience", value: "Teaching assistant" },
      { icon: Target, label: "Goal", value: "£800/mo top-up" },
    ],
    blurb: "A part-time teaching assistant with ~12 free evening hours a week and £500 to spend. Brilliant at explaining tricky things simply — but has never run a business.",
    interview: [
      { q: "How much do you want to earn — and is this a top-up or a full income?", a: "About £800 a month to start, alongside my job." },
      { q: "What do people already come to you for help with?", a: "Explaining tricky things simply — phonics, reading, helping anxious children." },
      { q: "How many hours a week can you realistically give this?", a: "Around 12, mostly evenings once the kids are in bed." },
      { q: "What could you comfortably spend to get started?", a: "No more than £500." },
    ],
    opps: [
      { name: "Evening Phonics Confidence Sessions", fit: 84, tone: "text-donkey-green", prop: "One-to-one online reading help for anxious 5–8 year-olds.", why: "Uses your exact strength, parents already ask you for it, and it costs almost nothing to start." },
      { name: "Reading-Ready Activity Packs", fit: 71, tone: "text-donkey-amber", prop: "Printable and digital phonics packs parents can use at home.", why: "Can grow beyond your hours — but you'd need to build an audience first." },
      { name: "TA Support Templates", fit: 63, tone: "text-donkey-grey", prop: "Ready-made planning templates for other teaching assistants.", why: "Easy to make, but the market is smaller and less proven." },
    ],
    recommend: "Start with Evening Phonics Confidence Sessions. It uses Priya's strongest skill, parents already ask her for exactly this, and she can test it this week for almost nothing — before touching the other ideas.",
    tasks: ["Offer three free taster sessions to parents you already know.", "Post once in two local parent groups and note the replies.", "Ask five parents what they'd honestly pay per session.", "Take one real booking — even at a small price."],
    evidence: ["Messages from interested parents", "A short waiting list of names", "One paid booking"],
  },
  {
    id: "marcus", name: "Marcus", tagline: "Recently redundant, wants a full income",
    signals: [
      { icon: Clock, label: "Time", value: "~30 hrs/wk" },
      { icon: Wallet, label: "Budget", value: "£2,000" },
      { icon: Briefcase, label: "Experience", value: "Retail manager" },
      { icon: Target, label: "Goal", value: "£2,000+/mo" },
    ],
    blurb: "A former retail manager, recently made redundant, with ~30 hours a week free and around £2,000 to invest. Highly organised and good with people — and needs to replace an income, not just top one up.",
    interview: [
      { q: "Is this a top-up or does it need to replace a wage?", a: "It needs to become a real income — ideally £2,000+ a month." },
      { q: "What did you do day-to-day in your last role?", a: "Ran a shop: rotas, stock, suppliers, sorting problems, training staff." },
      { q: "How much time can you give this each week?", a: "Full-time really — about 30 hours while I job-hunt." },
      { q: "What could you invest to get going?", a: "Around £2,000, but I want to spend it carefully." },
    ],
    opps: [
      { name: "Small-Shop Operations Fixer", fit: 82, tone: "text-donkey-green", prop: "Hands-on help for local independent shops with rotas, stock and suppliers.", why: "It's exactly what you did for years, local owners feel this pain, and you can start with one client." },
      { name: "Retail Staff Training Sessions", fit: 70, tone: "text-donkey-amber", prop: "Short practical training for new shop-floor staff.", why: "Strong fit for your skills, but you'll need a few shops to vouch for you first." },
      { name: "Managed Stock-Ordering Service", fit: 61, tone: "text-donkey-grey", prop: "Handling reordering and supplier chasing for busy owners.", why: "Useful, but it can get operationally heavy for one person early on." },
    ],
    recommend: "Start with the Small-Shop Operations Fixer. It's built on years of real experience, the problem is one local owners already feel, and a single paying shop proves it before you invest heavily.",
    tasks: ["Walk into five local independent shops and offer a free 30-minute review.", "Write down the three problems owners mention most.", "Propose one small paid fix to two owners.", "Land one paying client for a fortnight's help."],
    evidence: ["Notes from real shop-owner conversations", "One signed one-page agreement", "A first invoice paid"],
  },
  {
    id: "aisha", name: "Aisha", tagline: "Designer with a small following, side income",
    signals: [
      { icon: Clock, label: "Time", value: "~8 hrs/wk" },
      { icon: Wallet, label: "Budget", value: "£300" },
      { icon: Briefcase, label: "Experience", value: "Graphic designer" },
      { icon: Target, label: "Goal", value: "£400/mo side" },
    ],
    blurb: "A graphic designer in a full-time job with ~8 evening hours a week and about £300 to spend. Creative, with a small but engaged Instagram following, looking for a manageable side income.",
    interview: [
      { q: "Top-up or full income to begin with?", a: "A side income for now — maybe £400 a month around my job." },
      { q: "What do you make that people already react to?", a: "Clean, friendly brand designs — logos and simple identities for tiny businesses." },
      { q: "Realistically, how many hours a week?", a: "About 8, in the evenings." },
      { q: "What could you spend to start?", a: "Around £300." },
    ],
    opps: [
      { name: "Starter Brand Kits for Micro-Businesses", fit: 83, tone: "text-donkey-green", prop: "A fixed-price logo + colours + basics pack for brand-new tiny businesses.", why: "Matches your skill and audience, sells at a clear price, and fits into a few evenings." },
      { name: "Templated Social Media Kits", fit: 72, tone: "text-donkey-amber", prop: "Editable post templates small businesses can reuse.", why: "Scales nicely, but needs steady marketing to keep sales coming." },
      { name: "One-Off Instagram Audits", fit: 64, tone: "text-donkey-grey", prop: "Short paid reviews of a small brand's look and feed.", why: "Easy to offer, but low price and hard to make repeatable." },
    ],
    recommend: "Start with Starter Brand Kits for Micro-Businesses. It uses your strongest skill, your existing followers are exactly the buyers, and a fixed price makes it easy to sell in a spare evening.",
    tasks: ["Offer one free starter kit to a follower in exchange for feedback.", "Post the result and invite two paid enquiries.", "Set one clear fixed price and share it.", "Complete one paid kit end to end."],
    evidence: ["Comments and DMs asking about price", "One completed paid kit", "A repeat or referred enquiry"],
  },
];

const BUILD_ITEMS = [
  "Business name & positioning", "Logo, colours & brand identity", "Your offer & pricing",
  "Who your ideal customer is", "A simple website", "Connecting your own domain",
  "SEO foundations", "Taking payments or bookings", "Legal-page templates", "Your first marketing content",
];
const GROW_ITEMS = [
  "Capturing and organising leads", "A light customer record (CRM)", "Simple marketing campaigns",
  "Welcoming and onboarding customers", "Tracking what's working", "A clear next action, every week",
];

const STEPS = [
  { key: "interview", live: true, icon: MessageSquare, title: "Tell the Donkey about yourself",
    sub: "A calm, one-question-at-a-time chat about your skills, experience, goals, time and budget. No forms, no jargon." },
  { key: "opportunities", live: true, icon: Trophy, title: "Receive three opportunities",
    sub: "The Donkey turns your answers into three business ideas built around you — each with a simple fit score and an honest reason." },
  { key: "verdict", live: true, icon: CheckCircle2, title: "Get the Donkey's recommendation",
    sub: "A clear decision first — which one to start with, and why. You stay in control: choose it, ask for a rethink, or reject the lot." },
  { key: "test", live: true, icon: FlaskConical, title: "Test your idea before spending serious time or money",
    sub: "The Donkey turns your chosen idea into small, cheap tests — so you find out whether real people want it before you commit." },
  { key: "build", live: false, icon: Hammer, title: "Build the business",
    sub: "When you're ready to go further, the later plans help you shape and set up the real thing." },
  { key: "grow", live: false, icon: Rocket, title: "Launch and grow",
    sub: "Once you're live, the later plans help you find customers and keep momentum — with a clear next step every week." },
];

function Badge({ live }) {
  return live ? (
    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-donkey-green/40 text-donkey-green bg-donkey-green/10" data-testid="demo-badge-live">
      <CheckCircle2 className="w-3 h-3" /> Available now
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-donkey-amber/40 text-donkey-amber bg-donkey-amber/10" data-testid="demo-badge-preview">
      <Sparkles className="w-3 h-3" /> Preview — what later plans help you build
    </span>
  );
}

export default function Demo() {
  const navigate = useNavigate();
  const [persona, setPersona] = useState(null);
  const [step, setStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => { track("demo_started"); }, []);
  useEffect(() => { if (persona && !finished) track("demo_step_viewed", { step: step + 1 }); }, [step, finished, persona]);

  const total = STEPS.length;

  const goNext = () => {
    setStep((cur) => {
      if (cur < total - 1) return cur + 1;
      setFinished(true); setPlaying(false); track("demo_completed");
      return cur;
    });
  };

  // Gentle auto-advance
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (playing && persona && !finished) {
      timerRef.current = setTimeout(goNext, AUTOPLAY_MS);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [playing, step, finished, persona]);

  const next = () => goNext();
  const back = () => {
    if (finished) { setFinished(false); return; }
    if (step > 0) setStep((s) => s - 1);
    else { setPersona(null); setPlaying(false); }
  };
  const exit = () => navigate("/");
  const startJourney = () => { track("demo_start_prove_clicked"); navigate("/pricing"); };
  const viewPlans = () => { track("demo_view_plans_clicked"); navigate("/pricing"); };
  const choosePersona = (p) => { track("demo_persona_selected", { persona: p.id }); setPersona(p); setStep(0); setFinished(false); };

  const p = persona;
  const s = STEPS[step];

  return (
    <div className="relative overflow-hidden min-h-screen bg-donkey-bg grain flex flex-col" data-testid="demo-root">
      <PublicAmbient intensity={0.3} />
      {!persona ? (
        <Navbar />
      ) : (
      <header className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 h-16 flex items-center justify-between gap-3">
          <Logo />
          <div className="flex items-center gap-2">
            {persona && !finished && (
              <Button size="sm" variant="ghost" onClick={() => setPlaying((v) => !v)}
                className="text-donkey-warm hover:bg-white/5" aria-label={playing ? "Pause auto-play" : "Play auto-play"}
                aria-pressed={playing} data-testid="demo-autoplay-toggle">
                {playing ? <Pause className="w-4 h-4 sm:mr-1.5" /> : <Play className="w-4 h-4 sm:mr-1.5" />}
                <span className="hidden sm:inline">{playing ? "Pause" : "Auto-play"}</span>
              </Button>
            )}
            <Button size="sm" onClick={startJourney}
              className="bg-donkey-orange text-donkey-black font-semibold hidden sm:inline-flex" data-testid="demo-start-journey">
              Start my journey
            </Button>
            <Button size="sm" variant="ghost" onClick={exit}
              className="text-donkey-grey hover:text-donkey-warm" data-testid="demo-exit">
              <X className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Exit demo</span>
            </Button>
          </div>
        </div>
        {playing && persona && !finished && (
          <div className="h-0.5 w-full bg-white/5 overflow-hidden" data-testid="demo-autoplay-bar">
            <div key={step} className="h-full bg-donkey-orange/70 dk-autoplay-fill" style={{ animationDuration: `${AUTOPLAY_MS}ms` }} />
          </div>
        )}
      </header>
      )}

      <main className="relative z-10 flex-1 mx-auto max-w-4xl w-full px-5 sm:px-8 py-8 sm:py-10">
        {!persona ? (
          /* -------- Persona chooser -------- */
          <div data-testid="demo-chooser">
            <IntelligencePipeline />
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-donkey-orange" />
              <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange">Choose your example</p>
            </div>
            <KineticHeading as="h1" text="Who feels most like you?" className="text-2xl sm:text-3xl" />
            <p className="text-donkey-grey mt-2 max-w-2xl">Pick a fictional person to follow through the tour. You'll see how the Donkey works with their real-life situation — pick whoever's closest to yours.</p>
            <p className="text-[11px] text-donkey-grey/80 mt-2">Illustrative examples — not real customers.</p>
            <PersonaSelector personas={PERSONAS} onChoose={choosePersona} />

          </div>
        ) : !finished ? (
          <>
            <div className="mb-6" data-testid="demo-progress">
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange">Step {step + 1} of {total} · Following {p.name}</p>
                <Badge live={s.live} />
              </div>
              <div className="h-1.5 w-full bg-white/8 rounded-full overflow-hidden">
                <div className="h-full bg-donkey-orange transition-all duration-500" style={{ width: `${((step + 1) / total) * 100}%` }} />
              </div>
            </div>

            <div className="flex items-start gap-3 mb-2">
              <s.icon className="w-6 h-6 text-donkey-orange shrink-0 mt-1" />
              <div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-donkey-warm">{s.title}</h1>
                <p className="text-donkey-grey mt-2 leading-relaxed max-w-2xl">{s.sub}</p>
              </div>
            </div>
            <p className="text-[11px] text-donkey-grey/80 mt-3 mb-6">Illustrative example — not a real customer.</p>

            <div data-testid={`demo-step-${step}`}>{renderStep(s.key, p)}</div>

            <div className="mt-10 flex items-center justify-between gap-3">
              <Button variant="ghost" onClick={back}
                className="text-donkey-grey hover:text-donkey-warm" data-testid="demo-back">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
              </Button>
              <button onClick={startJourney} className="text-xs text-donkey-grey hover:text-donkey-warm underline underline-offset-4 sm:hidden" data-testid="demo-start-journey-mobile">
                Start my journey
              </button>
              <Button onClick={next}
                className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 h-12 shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 transition-all"
                data-testid="demo-next">
                {step < total - 1 ? "Next" : "See the finish"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-8" data-testid="demo-finish">
            <DonkeyMascot size={120} className="mx-auto mb-6" />
            <KineticHeading as="h1" text="Ready to find out what you could build?" className="text-3xl sm:text-4xl" />
            <p className="mt-4 text-donkey-grey max-w-xl mx-auto leading-relaxed">
              That's the whole journey — from what you already know, to an idea you can test, to a business you can build and grow.
              Prove is where it starts: your full Donkey's Verdict and your first month of validation.
            </p>
            <div className="mt-9 flex flex-wrap gap-4 justify-center">
              <Button onClick={startJourney}
                className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 h-12 shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 transition-all"
                data-testid="demo-start-prove">
                Start with Prove <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={viewPlans}
                className="border-donkey-line bg-transparent text-donkey-warm hover:border-donkey-orange/60 hover:bg-white/5 rounded-full px-7 h-12"
                data-testid="demo-view-plans">
                View plans
              </Button>
            </div>
            <button onClick={() => { setFinished(false); setPersona(null); setStep(0); }} className="mt-6 text-sm text-donkey-grey hover:text-donkey-warm block mx-auto" data-testid="demo-restart">
              Try another example
            </button>
            <button onClick={exit} className="mt-3 text-sm text-donkey-grey hover:text-donkey-warm" data-testid="demo-finish-exit">
              Back to home
            </button>
          </div>
        )}
      </main>

      <style>{`
        .dk-autoplay-fill{width:0;animation-name:dkAutoplay;animation-timing-function:linear;animation-fill-mode:forwards}
        @keyframes dkAutoplay{from{width:0}to{width:100%}}
        @media (prefers-reduced-motion: reduce){.dk-autoplay-fill{animation:none;width:100%}}
      `}</style>
    </div>
  );
}

function renderStep(key, p) {
  if (key === "interview") {
    return (
      <div className="space-y-4">
        <div className="card-donkey rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-donkey-grey mb-1">Meet the person in this example</p>
          <p className="text-donkey-warm text-sm leading-relaxed"><span className="font-semibold">{p.name}.</span> {p.blurb}</p>
        </div>
        {p.interview.map((it, i) => (
          <div key={i} className="space-y-2">
            <div className="flex gap-2.5">
              <DonkeyMark size={26} className="shrink-0 mt-1" />
              <div className="card-donkey rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-donkey-warm/90">{it.q}</div>
            </div>
            <div className="flex justify-end">
              <div className="rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm bg-donkey-orange/15 border border-donkey-orange/25 text-donkey-warm max-w-[80%]">{it.a}</div>
            </div>
          </div>
        ))}
        <p className="text-xs text-donkey-grey">The real interview adapts to your answers, one question at a time — usually 12–16 questions.</p>
      </div>
    );
  }
  if (key === "opportunities") {
    return (
      <div className="grid md:grid-cols-3 gap-4">
        {p.opps.map((o, i) => (
          <div key={i} className="card-donkey rounded-2xl p-5 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange">Idea {i + 1}</p>
              <span className={`font-mono text-lg ${o.tone}`}>{o.fit}<span className="text-xs text-donkey-grey">/100</span></span>
            </div>
            <h3 className="font-heading font-semibold text-donkey-warm">{o.name}</h3>
            <p className="text-sm text-donkey-grey mt-2">{o.prop}</p>
            <p className="text-xs text-donkey-warm/80 mt-3"><span className="text-donkey-grey">Why it suits {p.name}: </span>{o.why}</p>
          </div>
        ))}
      </div>
    );
  }
  if (key === "verdict") {
    return (
      <div className="space-y-5">
        <div className="card-donkey rounded-2xl p-6 border-l-2 border-donkey-green">
          <p className="text-xs uppercase tracking-[0.18em] text-donkey-green mb-1.5 flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5" /> The Donkey recommends
          </p>
          <p className="text-white/90 text-sm leading-relaxed">{p.recommend}</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {[["Choose this opportunity", "bg-donkey-orange text-donkey-black"],
            ["Ask for a rethink", "border border-donkey-line text-donkey-warm bg-transparent"],
            ["None of these feel right", "border border-donkey-line text-donkey-warm bg-transparent"]].map(([label, cls]) => (
            <div key={label} className={`rounded-full px-4 py-2.5 text-sm text-center font-semibold ${cls}`}>{label}</div>
          ))}
        </div>
        <p className="text-xs text-donkey-grey">You're always in control — the Donkey advises, you decide. Choosing never charges you or launches anything automatically.</p>
      </div>
    );
  }
  if (key === "test") {
    return (
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-donkey rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-donkey-grey mb-3">{p.name}'s first small tests</p>
          <ul className="space-y-2.5">
            {p.tasks.map((t, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-donkey-warm/90">
                <span className="mt-1 w-4 h-4 rounded border border-donkey-orange/50 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="card-donkey rounded-2xl p-5">
          <p className="text-xs uppercase tracking-[0.15em] text-donkey-grey mb-3">Real evidence the Donkey helps gather</p>
          <ul className="space-y-2.5">
            {p.evidence.map((e, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-donkey-warm/90">
                <CheckCircle2 className="w-4 h-4 text-donkey-green shrink-0 mt-0.5" /> {e}
              </li>
            ))}
          </ul>
          <p className="text-xs text-donkey-grey mt-4">Interest is never treated as proof. Only real signals — replies, sign-ups, a paid booking — count.</p>
        </div>
      </div>
    );
  }
  if (key === "build") {
    return (
      <div>
        <div className="grid sm:grid-cols-2 gap-3">
          {BUILD_ITEMS.map((it, i) => (
            <div key={i} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-donkey-warm/90">
              <Hammer className="w-4 h-4 text-donkey-orange shrink-0" /> {it}
            </div>
          ))}
        </div>
        <p className="text-xs text-donkey-grey mt-5 flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 text-donkey-amber shrink-0 mt-0.5" />
          These are later, paid stages — shown here as a preview of what they help you produce once your idea has proven demand, not a live result in this demo.
        </p>
      </div>
    );
  }
  if (key === "grow") {
    return (
      <div>
        <div className="grid sm:grid-cols-2 gap-3">
          {GROW_ITEMS.map((it, i) => (
            <div key={i} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-donkey-warm/90">
              <Rocket className="w-4 h-4 text-donkey-orange shrink-0" /> {it}
            </div>
          ))}
        </div>
        <p className="text-xs text-donkey-grey mt-5 flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 text-donkey-amber shrink-0 mt-0.5" />
          A preview of the later journey on higher plans — no live campaigns, customers or results are created in this demo.
        </p>
      </div>
    );
  }
  return null;
}

// ===== Interactive character-selection: profiles waiting to be analysed. =====
function ProfileNode({ pp, index, inView, reduce, active }) {
  const gid = `scan-${pp.id}`;
  return (
    <div className="relative mx-auto mb-3 w-[84px] h-[84px]">
      <svg viewBox="0 0 84 84" className="absolute inset-0 w-full h-full" aria-hidden>
        {[36, 28, 20].map((r, i) => (
          <motion.circle key={r} cx="42" cy="42" r={r} fill="none"
            stroke="rgba(249,115,22,0.35)" strokeWidth="1"
            initial={reduce ? false : { opacity: 0, scale: 0.6 }}
            animate={inView ? { opacity: 0.4 - i * 0.08, scale: 1 } : {}}
            style={{ transformOrigin: "42px 42px" }}
            transition={{ delay: 0.4 + index * 0.18 + i * 0.1, duration: 0.5, ease: EASE }} />
        ))}
        {[0, 1, 2, 3].map((k) => {
          const a = (k / 4) * Math.PI * 2;
          const cx = 42 + Math.cos(a) * 36, cy = 42 + Math.sin(a) * 36;
          return (
            <motion.circle key={k} cx={cx} cy={cy} r="2.2" fill="#F5A623"
              initial={reduce ? false : { opacity: 0 }}
              animate={inView ? { opacity: active ? 1 : 0.6 } : {}}
              transition={{ delay: 0.6 + index * 0.18 + k * 0.08, duration: 0.3 }} />
          );
        })}
      </svg>
      {!reduce && (
        <motion.svg viewBox="0 0 84 84" className="absolute inset-0 w-full h-full" aria-hidden
          style={{ transformOrigin: "center" }}
          initial={{ rotate: 0, opacity: 0 }}
          animate={inView ? { rotate: 360, opacity: [0, 1, 1, 0] } : {}}
          transition={{ delay: 0.5 + index * 0.18, duration: 1.25, ease: "linear" }}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#F97316" stopOpacity="0" />
              <stop offset="100%" stopColor="#FFE9B0" stopOpacity="0.95" />
            </linearGradient>
          </defs>
          <circle cx="42" cy="42" r="36" fill="none" stroke={`url(#${gid})`} strokeWidth="2.4"
            strokeLinecap="round" strokeDasharray="46 180" />
        </motion.svg>
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold text-xl transition-all duration-300
          ${active ? "bg-donkey-orange/25 border border-donkey-orange text-donkey-orange shadow-[0_0_20px_rgba(249,115,22,0.5)]" : "bg-donkey-orange/12 border border-donkey-orange/50 text-donkey-orange"}`}>
          {pp.name[0]}
        </span>
      </div>
    </div>
  );
}

function PersonaCard({ pp, index, inView, reduce, isHover, dimmed, locked, onHover, onSelect }) {
  return (
    <motion.div
      data-testid={`demo-persona-card-${pp.id}`}
      onMouseEnter={() => onHover(pp.id)}
      onMouseLeave={() => onHover(null)}
      initial={reduce ? false : { opacity: 0, y: 26 }}
      animate={inView ? { opacity: dimmed ? 0.5 : 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: 0.3 + index * 0.18, ease: EASE }}
      style={{ transform: isHover && !reduce ? "translateY(-4px)" : undefined }}
      className={`group relative overflow-hidden rounded-2xl p-5 flex flex-col card-donkey transition-[border-color,box-shadow,opacity] duration-300
        ${isHover ? "border-donkey-orange/70 shadow-[0_0_44px_rgba(249,115,22,0.20)]" : ""}`}
    >
      <span aria-hidden className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: "radial-gradient(420px circle at 50% 0%, rgba(249,115,22,0.12), transparent 70%)" }} />
      <span aria-hidden className={`pointer-events-none absolute inset-x-0 top-0 h-px transition-opacity duration-300 ${isHover ? "opacity-100" : "opacity-0"}`}
        style={{ background: "linear-gradient(90deg, transparent, #F5A623, #FFE9B0, #F5A623, transparent)" }} />

      <ProfileNode pp={pp} index={index} inView={inView} reduce={reduce} active={isHover} />

      <h3 className="font-heading font-semibold text-donkey-warm text-center">{pp.name}</h3>
      <p className="text-[11px] uppercase tracking-wider text-donkey-orange mt-0.5 text-center">{pp.tagline}</p>

      <div className="grid grid-cols-2 gap-1.5 mt-3">
        {pp.signals.map((sg, i) => (
          <motion.div key={sg.label}
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.55 + index * 0.18 + i * 0.1, duration: 0.35, ease: EASE }}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 min-w-0">
            <sg.icon className="w-3.5 h-3.5 text-donkey-orange shrink-0" />
            <div className="min-w-0">
              <p className="text-[8px] uppercase tracking-wider text-donkey-grey leading-none">{sg.label}</p>
              <p className="text-[11px] text-donkey-warm/90 leading-tight mt-0.5 truncate">{sg.value}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <p className="text-sm text-donkey-grey mt-3 flex-1 leading-relaxed">{pp.blurb}</p>

      <Button onClick={() => onSelect(pp)}
        className="mt-4 relative overflow-hidden bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-semibold rounded-full"
        data-testid={`demo-persona-${pp.id}`}>
        {isHover && !reduce && (
          <motion.span aria-hidden className="absolute top-0 bottom-0 w-1/3 -skew-x-12 pointer-events-none"
            style={{ background: "linear-gradient(100deg, transparent, rgba(255,255,255,0.5), rgba(255,233,176,0.4), transparent)" }}
            initial={{ left: "-40%" }} animate={{ left: "130%" }} transition={{ duration: 0.7, ease: EASE }} />
        )}
        <span className="relative inline-flex items-center">Follow {pp.name} <ArrowRight className="w-4 h-4 ml-1.5" /></span>
      </Button>

      <AnimatePresence>
        {locked && (
          <motion.div key="lock" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-donkey-black/80 backdrop-blur-sm"
            data-testid={`demo-persona-lock-${pp.id}`}>
            <motion.div initial={reduce ? false : { scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: EASE }} className="flex items-center gap-2 text-donkey-orange">
              <Lock className="w-5 h-5" />
              <span className="font-mono text-xs uppercase tracking-[0.22em]">Profile selected</span>
            </motion.div>
            <div className="mt-3 h-0.5 w-28 rounded-full bg-donkey-orange/25 overflow-hidden">
              <motion.div className="h-full bg-donkey-orange" initial={{ width: 0 }} animate={{ width: "100%" }}
                transition={{ duration: 0.44, ease: "linear" }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function PersonaSelector({ personas, onChoose }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [hovered, setHovered] = useState(null);
  const [locked, setLocked] = useState(null);

  const select = (pp) => {
    if (locked) return;
    setLocked(pp.id);
    if (reduce) { onChoose(pp); return; }
    setTimeout(() => onChoose(pp), 460);
  };
  const hoveredIndex = personas.findIndex((x) => x.id === hovered);

  return (
    <div ref={ref} className="mt-6" data-testid="demo-persona-grid">
      <div className="hidden md:block relative h-6 mb-1" aria-hidden>
        <motion.div className="absolute top-3 left-0 right-0 h-px bg-gradient-to-r from-donkey-orange/0 via-donkey-orange to-donkey-amber/80"
          style={{ transformOrigin: "left" }}
          initial={reduce ? false : { scaleX: 0, opacity: 0 }}
          animate={inView ? { scaleX: 1, opacity: 1 } : {}}
          transition={{ duration: 0.9, ease: EASE }} />
        <div className="absolute top-3 left-0 right-0 grid grid-cols-3">
          {personas.map((pp, i) => (
            <motion.span key={pp.id}
              className="justify-self-center w-2.5 h-2.5 rounded-full -translate-y-1/2"
              initial={reduce ? false : { scale: 0, opacity: 0 }}
              animate={inView ? { scale: 1, opacity: 1 } : {}}
              transition={{ delay: 0.35 + i * 0.22, duration: 0.4, ease: EASE }}
              style={{
                background: "#F97316",
                boxShadow: hoveredIndex === i ? "0 0 16px 3px rgba(249,115,22,0.9)" : "0 0 10px rgba(249,115,22,0.6)",
              }} />
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {personas.map((pp, i) => (
          <PersonaCard key={pp.id} pp={pp} index={i} inView={inView} reduce={reduce}
            isHover={hovered === pp.id} dimmed={!!hovered && hovered !== pp.id}
            locked={locked === pp.id}
            onHover={setHovered} onSelect={select} />
        ))}
      </div>
    </div>
  );
}
